<template>
<div>
  <div class="title-bar">
    <el-date-picker class="mr-r4" style="width: 140px;" v-model="selectedDate" type="date" :picker-options="{ disabledDate }" format="yyyy-MM-dd" @change="loadChartData" />
    <span class="title-h4">기간<em class="mr-l4">{{ datetimeLabel }}</em></span>
  </div>
  <div class="list-analytics analysis-chart" v-if="!showEmptyComponent" v-loading="loading">
    <div>
      <highcharts class="chart" :options="options" ref="highchart" />
      <el-scrollbar class="status">
        <ul class="list list-event">
          <li v-for="point in legendPoints" :key="point.key" :class="getLegendClass(point.series)" :style="{borderColor: point.color}" >
            <span class="number">{{ convertValuByUnit(point) }}</span>
            <span class="content" @click="onClickLegend(point.series)">
              <span>{{ point.series ? point.series.name : null }}</span>
            </span>
          </li>
        </ul>
      </el-scrollbar>
    </div>
  </div>
  <div v-else class="empty_box">
    <span class="empty_text">데이터가 없습니다.</span>
  </div>
</div>
</template>

<script>
import { cloneDeep, last, findLast, clone } from 'lodash';
import { isEmpty } from '@/utils/basic';
import { getDateToString } from '@/utils/date';
import { convertValuByUnit } from '@/utils/number';
import { lineOptions } from '@/constants/chart-options';
import ApmCountAPI from '@/api/performance/apm/count-info';

export default {
  name: 'WEumLineChart',
  props: {
    tabName: String,
    params: Object,
    visible: Boolean,
  },
  data() {
    return {
      options: cloneDeep(lineOptions),
      legendPoints: [],
      selectedDate: new Date(),
      datetimeLabel: null,
      showEmptyComponent: false,
      loading: false,
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(flag) {
        if (flag) {
          this.selectedDate = new Date();
        }
        this.loadChartData();
      },
    },
  },
  mounted() {
    const me = this;
    Object.assign(this.options.chart, {
      zoomType: 'x',
      height: 97,
      events: {
        selection(e) {
          const minX = e.xAxis[0].min.toFixed(0);
          const maxX = e.xAxis[0].max.toFixed(0);

          me.$emit('load', { startDatetime: minX, endDatetime: maxX });
          return false;
        },
      },
    });

    Object.assign(this.options.plotOptions.series, {
      connectNulls: false,
      point: {
        events: {
          mouseOver() {
            me.setCustomTooltip();
          },
        },
      },
      events: {
        mouseOut() {
          me.setCustomTooltip('init');
        },
      },
    });

    // 툴팁 표현
    Object.assign(this.options.tooltip, {
      formatter() {
        let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss');
        this.points.forEach((point) => {
          tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${convertValuByUnit(point.y, 'ms')}</b><br/>`;
        });
        return tooltip;
      },
    });
  },
  methods: {
    /**
     * 오늘 이후의 날짜는 비활성화
     * @param  {[type]} datetime [description]
     * @return {[type]}          [description]
     * @author jda
     */
    disabledDate(datetime) {
      return datetime > Date.now();
    },
    /**
     * 차트 옆에 툴팁 겸 범례를 표현한다.
     * 1. type이 init일 경우 가장 최근의 데이터를 표현한다.
     * 2. 그 외의 데이터는 마우스 오버 했을 시 해당 포인트르르 표현한다.
     * @param  {[type]} type [description]
     * @author jda
     */
    setCustomTooltip(type) {
      if (isEmpty(this.$refs.highchart)) {
        return;
      }
      const chart = this.$refs.highchart.chart;
      const points = [];
      if (!isEmpty(type) && type === 'init') {
        chart.series.forEach((record) => {
          const point = last(record.points);
          if (point) {
            points.push(point);
          }
        });
      } else {
        chart.series.forEach((record) => {
          const point = record.searchPoint(event);
          if (point) {
            points.push(point);
          } else {
            const finalPoint = findLast(record.points);
            if (!isEmpty(finalPoint)) {
              points.push(finalPoint);
            }
          }
        });
      }
      this.legendPoints = points;
    },
    /**
     * 차트 데이터를 그려준다.
     * @return {[type]} [description]
     * @author jda
     */
    loadChartData() {
      const selectedDate = this.selectedDate;
      if (isEmpty(selectedDate)) {
        return;
      }

      if (this.visible) {
        const startDatetime = clone(selectedDate).setHours(0, 0, 0, 0);
        const endDatetime = clone(selectedDate).setHours(23, 59, 59, 59);
        this.datetimeLabel = isEmpty(selectedDate) ? null : `${getDateToString(startDatetime)} ~ ${getDateToString(endDatetime)}`;

        const params = Object.assign(clone(this.params), { tabName: this.tabName, startDatetime, endDatetime });
        this.loading = true;
        this.$emit('reset');
        ApmCountAPI.getEumRawChartData(params).then((response) => {
          this.loading = false;
          if (response.data.success) {
            const result = response.data.data;
            this.showEmptyComponent = isEmpty(result);
            this.options.series = result;

            this.$nextTick(() => {
              this.setCustomTooltip('init');
            });
          }
        });
      } else {
        this.showEmptyComponent = true;
        this.options.series = [];
        this.legendPoints = [];
      }
    },
    /**
     * 범례 보이기/숨김 처리를 한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onClickLegend(currentSeries) {
      if (currentSeries.visible) {
        this.legendClass = 'legend-off';
        currentSeries.setVisible(false, false);
      } else {
        this.legendClass = null;
        currentSeries.setVisible(true, false);
      }
    },
    /**
     * 범례 활성화/비활성화의 className을 반환한다.
     * @param  {[type]} currentSeries [description]
     * @return {[type]}               [description]
     * @author jda
     */
    getLegendClass(currentSeries) {
      if (isEmpty(currentSeries)) {
        return null;
      }
      return currentSeries.visible ? null : 'legend-off';
    },
    /**
     * 차트의 데이터를 단위변환 한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    convertValuByUnit(point) {
      return convertValuByUnit(point.y, 'ms', true, false);
    },
  },
};
</script>

<style lang="scss" scoped>
.empty_box {
  width: 100%;
  display: table;
  .empty_text {
    display: table-cell;
    vertical-align: middle;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/apm/components/chart/EumLineChart.vue